import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import Loading from "../../assets/Loading.json";
import { host } from "../../api/ApiCalls";
import { interests } from "../../utils/interests";
import { Star } from "lucide-react";
import { useAuthStore } from "../../store/authStore";

const UserProfile = ({ user: mainUser, setUser: mainSetUser }) => {
  const [user, setUser] = useState(mainUser || null); // 🟢 User State
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [imageChange, setImageChange] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingToBackend, setUploadingToBackend] = useState(false);
  const [imageUploaded, setImageUploaded] = useState(false); // 🟢 Avatar Upload Status
  const [formData, setFormData] = useState({
    name: "",
    bio: "",
    avatar: "",
    username: "",
    interests: [],
  });

  const navigate = useNavigate();

  // 📡 Fetch user data
  useEffect(() => {
    setLoading(true);
    const token = JSON.parse(localStorage.getItem("academiaHubToken")); // 🟢 Get token from localStorage
    axios
      .get(`${host}/api/auth/profile`, { withCredentials: true, headers: {
      Authorization: `Bearer ${token}`, // ✅ Send token manually
    }, })
      .then((res) => {
        setUser(res.data.user);
        setFormData({
          name: res.data.user.name || "",
          bio: res.data.user.bio || "",
          avatar: res.data.user.avatar || "",
          username: res.data.user.username || "",
          interests: res.data.user.interests || [],
        });
      })
      .catch((err) => {
        // toast.error("Failed to fetch profile!");
        console.log(err);
      })
      .finally(() => setLoading(false));
  }, []);

  // 📝 Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 📸 Handle Image Upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true); // 🟡 Show Loading Indicator
      const formDataImage = new FormData();
      formDataImage.append("image", file);
      const imageUploadRes = await axios.post(
        `${host}/api/upload/upload`,
        formDataImage,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setFormData((prev) => ({ ...prev, avatar: imageUploadRes.data.url }));
      console.log("✅ Image Upload Response:", imageUploadRes.data.url);

      setImageUploaded(true); // ✅ Mark as Uploaded
      toast.success("Image uploaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed!");
    } finally {
      setUploading(false); // 🟢 Hide Loading
    }
  };

  const handleAvatarUpdate = async () => {
    setUploadingToBackend(true); // 🟡 Show Loading Indicator
    if (!imageUploaded) return toast.error("Please upload an image first!");
    try {
      const res = await axios.put(
        `${host}/api/auth/update-avatar`,
        { avatar: formData.avatar },
        { withCredentials: true }
      );
      setUser(res.data.user);
      mainSetUser(res.data.user);
      console.log("✅ Avatar Update Response:", res.data);
      setImageUploaded(false); // Reset state after update
      toast.success(res.data.message);
      setUploadingToBackend(false); // 🟢 Hide Loading
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update avatar!");
      setUploadingToBackend(false); // 🟢 Hide Loading
    }
  };

  // 🔄 Save Profile Changes
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${host}/api/auth/update-profile`, formData, {
        withCredentials: true,
      });
      setUser(res.data.user);
      mainSetUser(res.data.user);
      setEditMode(false);
      toast.success(res.data.message);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update profile!");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mt-14 bg-gradient-to-tr from-white/80 to-indigo-100/60 backdrop-blur-md shadow-2xl rounded-3xl border border-indigo-200 mb-8">
      {loading || !user ? (
        // <div className="flex items-center justify-center text-gray-800 m-8">
        //   <div className="w-80">
        //     <Lottie animationData={Loading} loop={true} />
        //   </div>
        // </div>
        <div className="animate-pulse flex flex-col gap-8">
    <h2 className="h-8 bg-gray-300 rounded w-1/3 mx-auto" />

    <div className="flex flex-col md:flex-row items-center md:items-start gap-10 px-6">
      {/* Avatar Skeleton */}
      <div className="flex flex-col items-center gap-4">
        <div className="w-32 h-32 rounded-full bg-gray-300" />
        <div className="h-10 w-32 bg-gray-200 rounded" />
      </div>

      {/* Info Skeleton */}
      <div className="flex-1 w-full flex flex-col gap-4">
        <div className="h-6 bg-gray-300 rounded w-3/4" />
        <div className="h-6 bg-gray-300 rounded w-1/2" />
        <div className="h-6 bg-gray-300 rounded w-full" />
        <div className="h-6 bg-gray-300 rounded w-2/3" />
        <div className="h-6 bg-gray-300 rounded w-1/3" />
        <div className="h-6 bg-gray-300 rounded w-3/5" />
        <div className="h-10 w-32 bg-gray-300 rounded mt-2" />
      </div>
    </div>
  </div>
      ) : (
        <>
          <h2 className="text-4xl font-extrabold text-center text-black-700 mb-8 tracking-wide drop-shadow-md">
            Profile Overview
          </h2>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-10 px-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center gap-4">
              <img
                src={formData.avatar}
                alt="Profile"
                className="w-32 h-32 rounded-full border-4 border-indigo-400 shadow-md hover:scale-105 transition"
              />

              {editMode && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  {uploading ? (
                    <p className="text-indigo-500 font-medium">Uploading...</p>
                  ) : uploadingToBackend ? (
                    <p className="text-indigo-500 font-medium">Updating...</p>
                  ) : (
                    <button
                      onClick={handleAvatarUpdate}
                      disabled={!imageUploaded}
                      className={`px-4 py-2 text-sm font-medium rounded-xl transition ${
                        imageUploaded
                          ? "bg-indigo-600 text-white hover:bg-indigo-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      Update Avatar
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Info Section */}
            <div className="flex-1 w-full">
              {editMode ? (
                <div className="flex flex-col gap-4">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-300"
                    placeholder="Enter your name"
                  />
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-300"
                    placeholder="Enter your username"
                  />
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-300"
                    placeholder="Tell us about yourself..."
                  />
                  {(user?.role === "student" || user?.role === "teacher") && (
                    <div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Your Interests
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {interests.map((interest) => {
                            const isSelected =
                              formData.interests.includes(interest);
                            return (
                              <button
                                type="button"
                                key={interest}
                                onClick={() => {
                                  setFormData((prev) => {
                                    const current = prev.interests;
                                    const alreadySelected =
                                      current.includes(interest);

                                    // Remove if already selected
                                    if (alreadySelected) {
                                      return {
                                        ...prev,
                                        interests: current.filter(
                                          (i) => i !== interest
                                        ),
                                      };
                                    }

                                    // Don't add more than 5
                                    if (current.length >= 5) return prev;

                                    return {
                                      ...prev,
                                      interests: [...current, interest],
                                    };
                                  });
                                }}
                                className={`px-4 py-1 rounded-full border text-sm transition ${
                                  isSelected
                                    ? "bg-indigo-600 text-white border-indigo-600"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                              >
                                {interest}
                              </button>
                            );
                          })}
                        </div>
                        {formData.interests.length >= 5 && (
                          <p className="text-xs text-red-500 mt-1">
                            You can select up to 5 interests only.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-4 mt-2">
                    <button
                      onClick={handleSubmit}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-xl"
                    >
                      Save
                    </button>
                    <button
                      // onClick={() => setEditMode(false)}
                      onClick={() => {
                        // ⏪ Reset avatar and formData to original user values
                        setFormData((prev) => ({
                          ...prev,
                          avatar: user.avatar || "",
                          name: user.name || "",
                          bio: user.bio || "",
                          username: user.username || "",
                          interests: user.interests || [],
                        }));
                        setImageUploaded(false); // 🔄 Cancel upload state
                        setEditMode(false);
                      }}
                      className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded-xl"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2 text-gray-800 text-lg">
                  <p>
                    <span className="font-semibold text-indigo-700">Name:</span>{" "}
                    {user?.name}
                  </p>
                  <p>
                    <span className="font-semibold text-indigo-700">
                      Username:
                    </span>{" "}
                    @{user?.username}
                  </p>
                  <p>
                    <span className="font-semibold text-indigo-700">
                      Email:
                    </span>{" "}
                    {user?.email}
                  </p>
                  <p>
                    <span className="font-semibold text-indigo-700">Role:</span>{" "}
                    <span className="uppercase bg-indigo-200 text-indigo-700 px-2 py-1 rounded-md text-sm">
                      {user?.role}
                    </span>
                  </p>
                  {user?.role === "teacher" && (
                    <p className="flex items-center gap-2">
                      <span className="font-semibold text-indigo-700">
                        Rating:
                      </span>{" "}
                      {/* {user?.rating ? ( */}
                      <Star className="h-5 w-5 text-yellow-400" />
                      <span>{user?.rating}</span>
                      {/* ) : ( */}
                      {/* <span className="text-gray-500">No rating available</span> */}
                      {/* )} */}
                    </p>
                  )}
                  <p>
                    <span className="font-semibold text-indigo-700">Bio:</span>{" "}
                    {user?.bio || "No bio added"}
                  </p>
                  {(user?.role === "student" || user?.role === "teacher") && (
                    <p>
                      <span className="font-semibold text-indigo-700">
                        Interests:
                      </span>{" "}
                      {user?.interests?.length > 0 ? (
                        user.interests.join(", ")
                      ) : (
                        <span className="text-gray-500">
                          No interests added
                        </span>
                      )}
                    </p>
                  )}
                  <button
                    onClick={() => setEditMode(true)}
                    className="mt-4 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-xl transition"
                  >
                    Edit Profile
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfile;
